
import React, { useEffect, useState } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, TooltipProps 
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, subDays, startOfDay, endOfDay, subHours, parseISO, startOfHour, endOfHour } from 'date-fns';
import { useDeviceChartData } from '@/hooks/useDeviceChartData';
import { fetchTriggerRange } from '@/utils/fetchTriggers';


interface DeviceActivityChartProps {
  days?: number;
  height?: number;
  title?: string;
  deviceId: string;
}

type MotionData = {
  hour: number;
  count: number;
  deviceName: string;
  formattedDate?: string;
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-slate-800 p-3 border rounded shadow-lg">
        <p className="font-semibold text-sm">
          {typeof label === 'number' ? 
            (payload[0]?.payload?.formattedDate ? 
              format(parseISO(payload[0].payload.formattedDate), 'MMM dd') : 
              `${label}:00 - ${Number(label) + 1}:00`) : 
            label}
        </p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {`Events: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DeviceActivityChart: React.FC<DeviceActivityChartProps> = ({ 
  days = 7,
  height = 200,
  title = "ESP32 Motion Detection",
  deviceId
}) => {
  const [chartData, setChartData] = useState<MotionData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const { toast } = useToast();

  // Use simplified real-time hook
  const { lastUpdate, triggerRefresh } = useDeviceChartData({
    deviceId,
    enabled: true
  });

  const processMotionEvents = (events: any[]) => {
    const data: Record<string, MotionData> = {};
    const now = new Date();
    
    console.log(`[Chart ${deviceId}] Processing ${events.length} motion events (${days === 0 ? 'hourly' : 'daily'} view)`);
    
    if (days === 0) {
      // Hourly view (last hour, by minute)
      const startTime = subHours(now, 1);
      
      // Initialize all minutes in the last hour
      for (let m = 0; m < 60; m++) {
        data[m] = {
          hour: m,
          count: 0,
          deviceName: deviceId
        };
      }

      // Filter events to only those from this device in the last hour
      const filteredEvents = events.filter(event => {
        const eventTime = new Date(event.created_at);
        const isThisDevice = event.device_id === deviceId;
        const isInLastHour = eventTime >= startTime && eventTime <= now;
        
        console.log(`[Chart ${deviceId}] Event ${event.id}: device=${event.device_id}, time=${event.created_at}, isThisDevice=${isThisDevice}, isInLastHour=${isInLastHour}`);
        
        return isThisDevice && isInLastHour;
      });

      console.log(`[Chart ${deviceId}] Filtered to ${filteredEvents.length} events for this device in last hour`);

      filteredEvents.forEach(event => {
        const eventTime = new Date(event.created_at);
        const minuteBucket = eventTime.getMinutes();
        
        if (data[minuteBucket]) {
          data[minuteBucket].count += 1;
        }
      });
    } else {
      // Daily view
      for (let d = 0; d < days; d++) {
        const date = subDays(now, days - 1 - d);
        const formattedDate = format(date, 'yyyy-MM-dd');
        data[formattedDate] = {
          hour: d,
          formattedDate: formattedDate,
          count: 0,
          deviceName: deviceId
        };
      }

      // Filter events to only those from this device in the specified date range
      const startDate = subDays(now, days);
      const filteredEvents = events.filter(event => {
        const eventTime = new Date(event.created_at);
        const isThisDevice = event.device_id === deviceId;
        const isInDateRange = eventTime >= startDate && eventTime <= now;
        
        return isThisDevice && isInDateRange;
      });

      console.log(`[Chart ${deviceId}] Filtered to ${filteredEvents.length} events for this device in last ${days} days`);

      filteredEvents.forEach(event => {
        const eventTime = new Date(event.created_at);
        const formattedDate = format(eventTime, 'yyyy-MM-dd');
        
        if (data[formattedDate]) {
          data[formattedDate].count += 1;
        }
      });
    }

    const processedData = Object.values(data).sort((a, b) => a.hour - b.hour);
    const eventsWithData = processedData.filter(d => d.count > 0);
    
    console.log(`[Chart ${deviceId}] Processed data summary:`, {
      totalInputEvents: events.length,
      dataPoints: processedData.length,
      eventsWithData: eventsWithData.length,
      totalCount: eventsWithData.reduce((sum, d) => sum + d.count, 0)
    });
    
    return processedData;
  };

  const fetchMotionEvents = async () => {
    try {
      setIsLoading(true);
      
      console.log(`[Chart ${deviceId}] 🔍 Fetching motion events for device: ${deviceId}`);
      
      // Calculate date range based on chart type
      const now = new Date();
      let startDate: Date;
      
      if (days === 0) {
        // Hourly view - last hour
        startDate = subHours(now, 1);
      } else {
        // Daily view - last N days
        startDate = subDays(now, days);
      }
      
      console.log(`[Chart ${deviceId}] Date range: ${startDate.toISOString()} to ${now.toISOString()}`);
      
      // Fetch motion events for this specific device and time range (paginated)
      let motionEvents: any[] = [];
      try {
        motionEvents = await fetchTriggerRange({
          deviceId,
          start: startDate.toISOString(),
          end: now.toISOString(),
        });
      } catch (error: any) {
        console.error(`[Chart ${deviceId}] Error fetching motion events:`, error);
        setDebugInfo(`Error: ${error.message}`);
        toast({
          title: "Data Fetch Error",
          description: `Failed to fetch motion events for ${deviceId}`,
          variant: "destructive",
        });
        return;
      }


      console.log(`[Chart ${deviceId}] Successfully fetched ${motionEvents?.length || 0} motion events`);
      
      if (motionEvents && motionEvents.length > 0) {
        console.log(`[Chart ${deviceId}] Sample events:`, motionEvents.slice(0, 3));
      }
      
      const data = motionEvents || [];
      const processedData = processMotionEvents(data);
      setChartData(processedData);
      
      setDebugInfo(`Found ${data.length} events for this device in selected time range`);
      
    } catch (error: any) {
      console.error(`[Chart ${deviceId}] ❌ Unexpected error:`, error);
      setDebugInfo(`Unexpected error: ${error.message}`);
      toast({
        title: "Data Fetch Error",
        description: `Failed to fetch motion events for ${deviceId}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when component mounts or when real-time update occurs
  useEffect(() => {
    console.log(`[Chart ${deviceId}] 🔄 Triggering data fetch due to lastUpdate change:`, lastUpdate);
    fetchMotionEvents();
  }, [deviceId, days, lastUpdate]);

  const formatXAxisTick = (value: any) => {
    if (days === 0) {
      return `${value}m`;
    } else {
      const dataPoint = chartData.find(d => d.hour === value);
      return dataPoint?.formattedDate ? format(parseISO(dataPoint.formattedDate), 'MMM dd') : '';
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="py-3">
        <CardTitle className="text-md flex items-center justify-between">
          {title}
          <div className="flex items-center gap-2">
            <button 
              onClick={triggerRefresh}
              className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border rounded"
              title="Refresh data"
            >
              Refresh
            </button>
            {lastUpdate && (
              <span className="text-xs text-muted-foreground">
                Updated: {format(lastUpdate, 'HH:mm:ss')}
              </span>
            )}
          </div>
        </CardTitle>
        <CardDescription>
          {days === 0
            ? "Motion events detected in the last hour, grouped by minute" 
            : `Motion events detected in the last ${days} days, grouped by day`}
          {debugInfo && (
            <div className="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded">
              Debug: {debugInfo}
            </div>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-1 pt-0">
        {isLoading ? (
          <div className="flex justify-center items-center h-[200px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis 
                dataKey="hour" 
                tickFormatter={formatXAxisTick}
                label={{ 
                  value: days === 0 ? 'Minutes' : 'Day', 
                  position: 'insideBottom', 
                  offset: -5 
                }}
              />
              <YAxis 
                allowDecimals={false}
                label={{ value: 'Events', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="count"
                fill="#4f46e5"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col justify-center items-center h-[200px] text-muted-foreground">
            <div>No motion data available for the selected period</div>
            <div className="text-xs mt-2">Device ID: {deviceId}</div>
            <button 
              onClick={triggerRefresh}
              className="mt-2 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border rounded"
            >
              Try Refresh
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceActivityChart;
